import html2canvas from 'html2canvas';

/**
 * Pre-load an image and return a promise
 */
const preloadImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
};

/**
 * Generate a thumbnail image from the cover page of LivePreview
 * @param {string} previewElementId - ID of the preview container
 * @returns {Promise<string|null>} - Base64 string of the thumbnail image for upload, or null on error
 */
export const generatePreviewThumbnail = async (previewElementId = 'live-preview-container') => {
  // Try to find the preview container by ID, or look for the live-preview class
  let previewContainer = document.getElementById(previewElementId);

  if (!previewContainer) {
    // Fallback: look for the preview-content div which wraps the LivePreview
    previewContainer = document.querySelector('.preview-content .live-preview');
  }

  if (!previewContainer) {
    // Another fallback: just look for live-preview class
    previewContainer = document.querySelector('.live-preview');
  }

  if (!previewContainer) {
    console.warn('Preview element not found:', previewElementId);
    return null;
  }

  // Always find the FIRST preview page (home page/cover page) - not the currently visible one
  const allPages = previewContainer.querySelectorAll('.preview-page');
  const coverPage = allPages[0]; // Always take the first page

  if (!coverPage) {
    console.warn('First page not found in preview');
    return null;
  }

  try {
    // Find the live-preview container to add exporting class
    const livePreview = previewContainer.querySelector('.live-preview') || previewContainer;
    const hadExportingClass = livePreview.classList.contains('exporting');

    // Store original styles
    const wasHidden = coverPage.classList.contains('hidden-for-slider');
    const originalStyles = {
      display: coverPage.style.display,
      visibility: coverPage.style.visibility,
      position: coverPage.style.position,
      width: coverPage.style.width,
      minHeight: coverPage.style.minHeight,
      maxHeight: coverPage.style.maxHeight,
      transform: coverPage.style.transform,
      overflow: coverPage.style.overflow,
    };

    // Add exporting class to force desktop layout
    if (!hadExportingClass) {
      livePreview.classList.add('exporting');
    }

    // Make cover page visible
    if (wasHidden) {
      coverPage.classList.remove('hidden-for-slider');
      coverPage.classList.add('visible');
    }

    // Set up for capture - header + tabs + disclaimer + study content
    const targetWidth = 900;
    const targetHeight = 520; // Header + tabs + disclaimer + study content

    coverPage.style.display = 'block';
    coverPage.style.visibility = 'visible';
    coverPage.style.position = 'relative';
    coverPage.style.width = `${targetWidth}px`;
    coverPage.style.minHeight = 'auto';
    coverPage.style.maxHeight = 'none';
    coverPage.style.transform = 'none';
    coverPage.style.overflow = 'hidden';

    // Pre-load header background images to ensure they render in html2canvas
    const headerBanner = coverPage.querySelector('.page-header-banner');
    if (headerBanner) {
      const bgImage = window.getComputedStyle(headerBanner).backgroundImage;
      const urlMatch = bgImage.match(/url\(["']?([^"')]+)["']?\)/);
      if (urlMatch && urlMatch[1]) {
        try {
          await preloadImage(urlMatch[1]);
        } catch (e) {
          console.warn('Failed to preload header image:', e);
        }
      }
    }

    // Wait for styles to apply and images to render
    await new Promise(resolve => setTimeout(resolve, 500));

    const canvas = await html2canvas(coverPage, {
      scale: 2,
      backgroundColor: '#ffffff',
      useCORS: true,
      allowTaint: true,
      logging: false,
      x: 0,
      y: 0,
      width: targetWidth,
      height: targetHeight,
      windowWidth: targetWidth,
      windowHeight: targetHeight,
      scrollX: 0,
      scrollY: 0,
      imageTimeout: 15000,
      onclone: (clonedDoc) => {
        const clonedPage = clonedDoc.querySelector('.preview-page');
        if (clonedPage) {
          clonedPage.style.textRendering = 'optimizeLegibility';
          clonedPage.style.webkitFontSmoothing = 'antialiased';
          clonedPage.style.width = `${targetWidth}px`;
          clonedPage.style.overflow = 'hidden';
          clonedPage.style.margin = '0';
          clonedPage.style.padding = '0';

          // Ensure header banner has proper background - use 100% width to show full image including Genentech logo
          const headerBanner = clonedPage.querySelector('.page-header-banner');
          if (headerBanner) {
            headerBanner.style.minHeight = '140px';
            headerBanner.style.width = '100%';
            headerBanner.style.backgroundSize = '100% auto';
            headerBanner.style.backgroundPosition = 'top left';
            headerBanner.style.backgroundRepeat = 'no-repeat';
            // Ensure background color as fallback
            if (!headerBanner.style.backgroundColor) {
              headerBanner.style.backgroundColor = '#1a365d';
            }
          }

          // Ensure header shows desktop layout with Genentech on right
          const pageHeader = clonedPage.querySelector('.page-header');
          if (pageHeader) {
            pageHeader.style.margin = '0';
            pageHeader.style.width = '100%';
          }

          const headerContent = clonedPage.querySelector('.page-header-content');
          if (headerContent) {
            headerContent.style.display = 'flex';
            headerContent.style.flexDirection = 'row';
            headerContent.style.justifyContent = 'flex-start';
            headerContent.style.alignItems = 'flex-start';
            headerContent.style.width = '100%';
            headerContent.style.padding = '20px 24px';
            headerContent.style.minHeight = '140px';
            headerContent.style.boxSizing = 'border-box';
          }

          // Ensure header text section doesn't overflow - limit to 60% to leave room for logo in background
          const headerText = clonedPage.querySelector('.page-header-text');
          if (headerText) {
            headerText.style.flex = '0 0 auto';
            headerText.style.maxWidth = '60%';
            headerText.style.minWidth = '0';
          }

          // Ensure title text is visible but constrained
          const headerTitle = clonedPage.querySelector('.page-header-title');
          if (headerTitle) {
            headerTitle.style.fontSize = '18px';
            headerTitle.style.whiteSpace = 'normal';
            headerTitle.style.wordBreak = 'break-word';
            headerTitle.style.color = '#ffffff';
            headerTitle.style.margin = '0';
            headerTitle.style.lineHeight = '1.3';
          }

          // Ensure subtitle is visible
          const headerSubtitle = clonedPage.querySelector('.page-header-subtitle');
          if (headerSubtitle) {
            headerSubtitle.style.fontSize = '11px';
            headerSubtitle.style.color = 'rgba(255, 255, 255, 0.9)';
            headerSubtitle.style.margin = '4px 0 0 0';
          }

          // Ensure TOC bar is styled properly
          const tocBar = clonedPage.querySelector('.cover-toc-bar');
          if (tocBar) {
            tocBar.style.display = 'flex';
            tocBar.style.backgroundColor = '#0366D3';
            tocBar.style.padding = '8px 16px';
          }

          // Ensure study header shows badge on right side
          const studyHeader = clonedPage.querySelector('.study-header-section');
          if (studyHeader) {
            studyHeader.style.display = 'flex';
            studyHeader.style.flexDirection = 'row';
            studyHeader.style.justifyContent = 'space-between';
            studyHeader.style.alignItems = 'flex-start';
            studyHeader.style.width = '100%';
            studyHeader.style.padding = '16px 24px';
            studyHeader.style.backgroundColor = '#ffffff';
          }

          // Ensure study type badge is visible
          const studyBadge = clonedPage.querySelector('.study-type-badge-large');
          if (studyBadge) {
            studyBadge.style.flexShrink = '0';
            studyBadge.style.padding = '8px 16px';
            studyBadge.style.borderRadius = '8px';
            studyBadge.style.color = '#ffffff';
            studyBadge.style.fontWeight = 'bold';
          }

          // Hide detailed study content for cleaner thumbnail
          const studyDetails = clonedPage.querySelector('.study-details-content');
          if (studyDetails) {
            studyDetails.style.display = 'none';
          }

          // Show study page content (objective, blurb, etc.)
          const studyPageContent = clonedPage.querySelector('.study-page-content');
          if (studyPageContent) {
            studyPageContent.style.display = 'block';
            studyPageContent.style.padding = '16px 24px';
          }

          // Hide footer elements for thumbnail
          const pageFooter = clonedPage.querySelector('.page-footer');
          if (pageFooter) {
            pageFooter.style.display = 'none';
          }

          const pageNavFooter = clonedPage.querySelector('.page-navigation-footer');
          if (pageNavFooter) {
            pageNavFooter.style.display = 'none';
          }

          const pageSpacer = clonedPage.querySelector('.page-content-spacer');
          if (pageSpacer) {
            pageSpacer.style.display = 'none';
          }
        }
      },
    });

    // Restore original styles
    coverPage.style.display = originalStyles.display;
    coverPage.style.visibility = originalStyles.visibility;
    coverPage.style.position = originalStyles.position;
    coverPage.style.width = originalStyles.width;
    coverPage.style.minHeight = originalStyles.minHeight;
    coverPage.style.maxHeight = originalStyles.maxHeight;
    coverPage.style.transform = originalStyles.transform;
    coverPage.style.overflow = originalStyles.overflow;
    if (wasHidden) {
      coverPage.classList.add('hidden-for-slider');
      coverPage.classList.remove('visible');
    }

    // Remove exporting class if we added it
    if (!hadExportingClass) {
      livePreview.classList.remove('exporting');
    }

    // Convert canvas to base64 string
    const base64String = canvas.toDataURL('image/jpeg', 0.95);
    return base64String;
  } catch (error) {
    console.error('Error generating thumbnail:', error);
    return null;
  }
};

export default generatePreviewThumbnail;
